import React from 'react';
import { useInView } from 'react-intersection-observer';
import { Heart, Users, Target, Zap } from 'lucide-react';
import BackgroundAnimation from '../components/BackgroundAnimation';

const About = () => {
  const { ref: storyRef, inView: storyInView } = useInView({ threshold: 0.1, triggerOnce: true });

  const values = [
    {
      icon: Heart,
      title: 'Client-Centric',
      description: 'Every strategy begins with understanding how we can amplify your success.',
      emoji: '💚'
    },
    {
      icon: Zap,
      title: 'Relentless Innovation',
      description: 'We continuously expand what intelligent automation can achieve for enterprises.',
      emoji: '⚡'
    },
    {
      icon: Users,
      title: 'True Partnership',
      description: 'Your victories are ours. We thrive when your business flourishes.',
      emoji: '🤝'
    },
    {
      icon: Target,
      title: 'Outcome Obsessed',
      description: 'Every initiative is measured. If it doesn\'t deliver impact, we pivot.',
      emoji: '🎯'
    }
  ];

  return (
    <div className="min-h-screen page-transition" style={{ background: 'var(--bg-primary)', paddingTop: '80px', position: 'relative' }}>
      <BackgroundAnimation />
      {/* Hero */}
      <section className="py-24" style={{ padding: '100px 7.6923%', background: 'var(--bg-primary)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="display-huge mb-6" style={{ color: 'var(--text-primary)' }}>
            Our Journey &
            <span className="block" style={{ color: 'var(--brand-primary)' }}>
              Mission
            </span>
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            A founder's bold pursuit to transform operational chaos into streamlined excellence.
          </p>
        </div>
      </section>

      {/* Founder Story */}
      <section
        ref={storyRef}
        className="py-24"
        style={{ padding: '100px 7.6923%', background: 'var(--bg-primary)' }}
      >
        <div className="max-w-5xl mx-auto">
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${storyInView ? 'fade-in' : 'opacity-0'}`}>
            {/* Left: Founder Image */}
            <div
              className="rounded-lg overflow-hidden"
              style={{
                background: 'rgba(0, 255, 209, 0.05)',
                border: '1px solid var(--border-subtle)',
                minHeight: '500px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <img
                src="https://d2ol7oe51mr4n9.cloudfront.net/user_3Gf2Ac4LN1Y0kPEMECOhIpwha13/94b011b1-e83b-4f78-b8fd-5687973b3515.jpg"
                alt="Naveen Dadi - Founder & CEO of American Prime LLC and CredX USA"
                className="w-full h-auto object-cover rounded-t-lg"
                style={{ maxHeight: '420px', objectFit: 'cover', objectPosition: 'center top' }}
                onError={(e) => {
                  // Prefer a self-hosted copy once founder-naveen-dadi.jpg is added
                  // to /public, so the page never depends on the CDN staying up.
                  if (!e.target.src.endsWith('/founder-naveen-dadi.jpg')) {
                    e.target.src = '/founder-naveen-dadi.jpg';
                  } else {
                    e.target.style.display = 'none';
                  }
                }}
              />
              <div className="text-center p-6">
                <div className="heading-2" style={{ color: 'var(--text-primary)' }}>
                  Naveen Dadi
                </div>
                <div className="body-medium" style={{ color: 'var(--text-muted)' }}>
                  Founder & CEO, American Prime LLC
                </div>
                <div className="body-medium" style={{ color: 'var(--text-muted)' }}>
                  Founder & CEO, CredX USA
                </div>
                <div className="body-medium" style={{ color: 'var(--text-muted)', marginTop: '4px' }}>
                  <a href="mailto:dadi@americanprimellc.com" style={{ color: 'var(--brand-primary)' }}>dadi@americanprimellc.com</a>
                </div>
              </div>
            </div>

            {/* Right: Story */}
            <div className="space-y-6">
              <div className="heading-2" style={{ color: 'var(--brand-primary)' }}>
                The Challenge
              </div>
              <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                Naveen Dadi is the Founder & CEO of two American companies — <strong style={{ color: 'var(--text-primary)' }}>American Prime LLC</strong>, an AI automation firm, and <strong style={{ color: 'var(--text-primary)' }}>CredX USA</strong>. Through years of building ventures, he observed recurring patterns that held organizations back:
              </p>
              <ul className="space-y-3">
                <li className="body-medium flex items-start space-x-3">
                  <span style={{ color: 'var(--brand-primary)' }}>•</span>
                  <span style={{ color: 'var(--text-secondary)' }}>Fragmented software ecosystems draining valuable resources</span>
                </li>
                <li className="body-medium flex items-start space-x-3">
                  <span style={{ color: 'var(--brand-primary)' }}>•</span>
                  <span style={{ color: 'var(--text-secondary)' }}>Talented teams buried under monotonous, time-consuming tasks</span>
                </li>
                <li className="body-medium flex items-start space-x-3">
                  <span style={{ color: 'var(--brand-primary)' }}>•</span>
                  <span style={{ color: 'var(--text-secondary)' }}>Escalating expenses yielding diminishing competitive advantage</span>
                </li>
                <li className="body-medium flex items-start space-x-3">
                  <span style={{ color: 'var(--brand-primary)' }}>•</span>
                  <span style={{ color: 'var(--text-secondary)' }}>Leaders consumed by technology management rather than strategic growth</span>
                </li>
              </ul>

              <div className="pt-8">
                <div className="heading-2 mb-4" style={{ color: 'var(--brand-primary)' }}>
                  The Breakthrough
                </div>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  Naveen envisioned something revolutionary: <strong style={{ color: 'var(--text-primary)' }}>A unified AI command center capable of orchestrating every business function.</strong>
                </p>
                <p className="body-medium mt-4" style={{ color: 'var(--text-secondary)' }}>
                  Not another fragmented solution. Not another point product. A comprehensive operating system designed to bring order to operational complexity.
                </p>
              </div>

              <div className="pt-8">
                <div className="heading-2 mb-4" style={{ color: 'var(--brand-primary)' }}>
                  Our Purpose
                </div>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  American Prime emerged from this conviction: to democratize access to enterprise-caliber AI that seamlessly manages sales, customer success, operations, marketing, and financial workflows — unified in one intelligent platform.
                </p>
                <p className="body-medium mt-4" style={{ color: 'var(--text-secondary)' }}>
                  We're now empowering enterprises to consolidate 10+ disparate tools, reduce operational costs by 60%, and achieve scale without traditional friction.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24" style={{ padding: '100px 7.6923%', background: 'var(--bg-primary)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="display-large mb-4" style={{ color: 'var(--text-primary)' }}>
              Our
              <span style={{ color: 'var(--brand-primary)' }}> Guiding Principles</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              The foundational beliefs that shape every solution we craft
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="p-8 rounded dark-hover card-lift"
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid var(--border-subtle)'
                  }}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <div
                      className="w-12 h-12 rounded flex items-center justify-center"
                      style={{ background: 'rgba(0, 255, 209, 0.1)' }}
                    >
                      <Icon size={24} style={{ color: 'var(--brand-primary)' }} />
                    </div>
                    <span className="text-2xl">{value.emoji}</span>
                  </div>
                  <h3 className="heading-3 mb-3" style={{ color: 'var(--text-primary)' }}>
                    {value.title}
                  </h3>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section
        className="py-24"
        style={{
          padding: '100px 7.6923%',
          background: 'rgba(0, 255, 209, 0.05)',
          borderTop: '1px solid var(--border-subtle)',
          borderBottom: '1px solid var(--border-subtle)'
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 text-center">
            <div>
              <div className="display-large" style={{ color: 'var(--brand-primary)' }}>500+</div>
              <div className="body-medium" style={{ color: 'var(--text-secondary)' }}>Businesses Served</div>
            </div>
            <div>
              <div className="display-large" style={{ color: 'var(--brand-primary)' }}>$2M+</div>
              <div className="body-medium" style={{ color: 'var(--text-secondary)' }}>Costs Saved</div>
            </div>
            <div>
              <div className="display-large" style={{ color: 'var(--brand-primary)' }}>98%</div>
              <div className="body-medium" style={{ color: 'var(--text-secondary)' }}>Client Satisfaction</div>
            </div>
            <div>
              <div className="display-large" style={{ color: 'var(--brand-primary)' }}>24/7</div>
              <div className="body-medium" style={{ color: 'var(--text-secondary)' }}>AI Support</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24" style={{ padding: '100px 7.6923%', background: 'var(--bg-primary)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="display-large mb-6" style={{ color: 'var(--text-primary)' }}>
            Ready to Transform
            <span className="block" style={{ color: 'var(--brand-primary)' }}>
              Your Business?
            </span>
          </h2>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
            Join the AI revolution. Let's build your future together.
          </p>
          <a
            href="https://calendly.com/americanprimeforyou"
            target="_blank"
            rel="noopener noreferrer"
            className="button-text dark-button-animate"
            style={{
              background: 'var(--brand-primary)',
              color: '#000000',
              padding: '14px 32px',
              borderRadius: '0px',
              border: 'none',
              textDecoration: 'none',
              display: 'inline-block'
            }}
          >
            Schedule a Demo
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;
